import { createSlice, current } from "@reduxjs/toolkit";
import { initialState } from "./initialState";
import { activatePatient, fetchBillingCodesOnBlur, fetchCPTCodes, fetchICDCodes, fetchMergePatients, getAllSelectInput, getPatientBasicDetails, mergePatients, validatePatients, getPatientLedgerList, loadClaimProcedures, loadProcedureActivities, getActivityOfProcedure, patientPaymentSummary, patientLedgerSummary, updatePatientData, patientCreditBalanceView } from "./asyncThunkAPI";
import { checkFormWarning, generateOtherBillingData, generatePatientBasicDetails, generatePatientInfoData, validateCPTICDCodes } from "./patientDataProcessor";
import { DEFAULTRELATION, SSN_VALID_LENGTH, SSN_VALID_LENGTH_SMALL } from "../../../../utilities/staticConfigs";
import i18n from "../../../../utilities/i18n";
import moment from "moment";

const patientBetaSlice = createSlice({
    name: 'patientbeta',
    initialState,
    reducers: {
        resetToInitial: () => initialState,
        /**
         * to reset error showing varibale to null after show the error
         * @param {*} state 
         */
        resetNotifyWindow: (state) => {
            state.notifyMessage = "";
            state.showNotification = false;
            state.statusTag = "";
        },
        clearList: (state, action) => {
            state[action.payload] = [];
            state.cptError = null;
        },
        showModal: (state, action) => {
            const { value, key } = action.payload;
            state[key] = value;
        },
        showPatientLedgerModal: (state, action) => {
            const { value, patientId } = action.payload;
            state.patientLedger.isOpen = value;
            state.patientLedger.patientId = patientId;
        },
        setMergePatientSelected: (state, actions) => {
            state.mergePatientSelected = actions.payload;
            state.patientMergeList = [];
            state.isCheckedAll = false;
            state.patientMergeModal = false;
            state.patientValidationModal = false;
        },
        // Handles basic top-level patient fields (Name, etc.)
        basicFieldChange: (state, actions) => {
            const { patientBasicInfo, patientFormWarnings } = current(state);
            const { name, value } = actions.payload;
            let formattedValue = value;

            if (name === 'last_name' || name === 'first_name' || name === 'middle_name') {
                formattedValue = value.replace(/[^A-Za-z0-9.\s]/g, '');
            }

            const basic_data = {
                ...patientBasicInfo,
                [name]: formattedValue ?? null
            };

            state.patientBasicInfo = basic_data;
            state.patientFormWarnings = checkFormWarning(basic_data, state.patientInfoData, patientFormWarnings,state.isInsuranceAdded);
            state.formWarningStatus = !state.formWarningData;
        },
        // General handler for patientInfoData top-level fields
        onHandleChangeValue: (state, action) => {
            const { name, value, type, checked } = action.payload;
            const { patientInfoData: prev, relationList, referringProvider } = state;

            // 1. Correctly determine the value (checkboxes use 'checked')
            let finalValue = type === "checkbox" ? checked : value;

            // Helper to find 'Self' relation ID
            const relSelfItem = relationList.find(
                (el) => el.name.trim().toLowerCase() === DEFAULTRELATION
            );

            if (name === "guarantor_relationship") {
                let newData = { ...prev, guarantor_relationship: finalValue };

                // If switching FROM 'Self' to something else, reset all guarantor fields
                if (prev.guarantor_relationship === relSelfItem?.id && prev.guarantor_relationship !== finalValue) {
                    newData = {
                        ...newData,
                        guarantor_first_name: null,
                        guarantor_middle_name: null,
                        guarantor_last_name: null,
                        guarantor_suffix: null,
                        guarantor_address_as_patient: false,
                        guarantor_location_data: { city: "", country:"", state:"", street1:"", street2: "", zip_code: "" },
                        guarantor_home_phone: "1",
                        guarantor_work_phone: "1",
                        guarantor_cell_phone: "1",
                        guarantor_email: null,
                    };
                }

                // If emergency is synced to guarantor, update emergency relationship too
                if (prev.emergency_as_guarantor) {
                    newData.emergency_relationship = finalValue;
                    state.emergencySelf = (finalValue === relSelfItem?.id);
                }

                state.guaratorSelf = (finalValue === relSelfItem?.id);
                state.patientInfoData = newData;

            } else if (name === "guarantor_address_as_patient") {
                let newData = { ...prev, [name]: finalValue };

                if (finalValue) {
                    // Sync Patient Address to Guarantor
                    newData.guarantor_location_data = { ...prev.location_data };
                    newData.guarantor_home_phone = prev.home_phone || "1";
                    newData.guarantor_work_phone = prev.work_phone || "1";
                    newData.guarantor_cell_phone = prev.cell_phone || "1";
                    newData.guarantor_email = prev.email;
                } else {
                    // Reset Guarantor Address
                    newData.guarantor_location_data = { city: "", country:"", state:"", street1:"", street2: "", zip_code: "" };
                    newData.guarantor_home_phone = "1";
                    newData.guarantor_work_phone = "1";
                    newData.guarantor_cell_phone = "1";
                    newData.guarantor_email = "";
                }
                state.patientInfoData = newData;

            } else if (name === "emergency_as_guarantor") {
                let newData = { ...prev, [name]: finalValue };
                state.emergencySelf = (relSelfItem?.id === prev.guarantor_relationship);

                if (finalValue) {
                    // Sync Guarantor details to Emergency
                    newData = {
                        ...newData,
                        emergency_location_data: { ...prev.guarantor_location_data },
                        emergency_first_name: prev.guarantor_first_name ?? null,
                        emergency_last_name: prev.guarantor_last_name ?? null,
                        emergency_middle_name: prev.guarantor_middle_name ?? null,
                        emergency_suffix: prev.guarantor_suffix ?? null,
                        emergency_relationship: prev.guarantor_relationship ?? null,
                        emergency_cell_phone: prev.guarantor_cell_phone ?? "1",
                        emergency_home_phone: prev.guarantor_home_phone ?? "1",
                        emergency_work_phone: prev.guarantor_work_phone ?? "1",
                        emergency_email: prev.guarantor_email ?? null,
                    };
                } else {
                    // Reset Emergency details
                    newData.emergency_location_data = { city: "", country:"", state:"", street1:"", street2: "", zip_code: "" };
                    newData.emergency_first_name = null;
                    newData.emergency_last_name = null;
                    newData.emergency_middle_name = null;
                    newData.emergency_suffix =null;
                    newData.emergency_cell_phone = "1";
                    newData.emergency_home_phone = "1";
                    newData.emergency_work_phone = "1";
                    newData.emergency_email = null;
                }
                state.patientInfoData = newData;

            } else if (name === "emergency_relationship") {
                let newData = { ...prev, [name]: finalValue };

                // If switching FROM 'Self' to something else, reset emergency fields
                if (prev.emergency_relationship === relSelfItem?.id && prev.emergency_relationship !== finalValue) {
                    newData = {
                        ...newData,
                        emergency_first_name: null,
                        emergency_middle_name: null,
                        emergency_last_name: null,
                        emergency_suffix: null,
                        emergency_cell_phone: "1",
                        emergency_home_phone: "1",
                        emergency_work_phone: "1",
                        emergency_location_data: { city: "", country:"", state:"", street1:"", street2: "", zip_code: "" }
                    };
                }

                if (finalValue === relSelfItem?.id) {
                    state.emergencySelf = true;
                    newData.emergency_as_guarantor = (prev.guarantor_relationship === finalValue);
                } else {
                    state.emergencySelf = false;
                    if (prev.guarantor_relationship !== finalValue) newData.emergency_as_guarantor = false;
                }
                state.patientInfoData = newData;

            } else if (name === "service_location_value") {
                if (finalValue) {
                    const [id, type] = finalValue.split("||");
                    state.patientInfoData.service_location = { id, location_type: type };
                    state.patientInfoData.service_location_value = finalValue;
                }
            } else {
                // Default handler for all other fields
                state.patientInfoData[name] = finalValue;

                // Referral Group logic
                if (name === 'referring_provider') {
                    const provider = referringProvider.find(ele => ele.id == finalValue);
                    if (provider?.referral_provider_group) {
                        state.patientInfoData.referring_provider_group = provider.referral_provider_group;
                    }
                }
            }

            // Global Warning Refresh
            state.patientFormWarnings = checkFormWarning(state.patientBasicInfo, state.patientInfoData, state.patientFormWarnings,state.isInsuranceAdded);
            state.formWarningStatus = !state.formWarningData;
        },
        // Specialized handler for Address/Location objects
        onHandleLocationDataChange: (state, action) => {
            const { patientFormWarnings } = current(state);
            const { name, value, type, key } = action.payload;

            // Handle Street Validation logic
            if (name === 'street2') {
                if (key === "location_data") {
                    state.streetValidity.street = state.patientInfoData.location_data.street1 === null;
                } else if (key === "guarantor_location_data") {
                    state.streetValidity.guarantorStreet = state.patientInfoData.guarantor_location_data.street1 === null;
                } else if (key === "emergency_location_data") {
                    state.streetValidity.emergencyStreet = state.patientInfoData.emergency_location_data.street1 === null;
                }
            }

            if (type === "key") {
                state.patientInfoData[key] = {
                    ...state.patientInfoData[key],
                    [name]: value ?? null
                };
            } else if (type === "value_set") {
                // Used for GeoCode search results
                state.patientInfoData[name] = {
                    ...state.patientInfoData[name],
                    ...value
                };
            } else {
                state.patientInfoData = {
                    ...state.patientInfoData,
                    ...value
                };
            }

            state.patientFormWarnings = checkFormWarning(state.patientBasicInfo, state.patientInfoData, patientFormWarnings,state.isInsuranceAdded);
            state.formWarningStatus = !state.formWarningData;
        },
        onHandleSSNChange: (state, action) => {
            let { name, value } = action.payload;
            let formattedValue = String(value?.trim() ?? "").replace(/[^\d]/g, "").substr(0, 9);
            state.patientBasicInfo = {
                ...state.patientBasicInfo,
                [name]: formattedValue
            }

            if (
                formattedValue.length !== SSN_VALID_LENGTH &&
                formattedValue.length !== SSN_VALID_LENGTH_SMALL
            ) {
                state.isValidSSN = false;
            }

            if (
                !formattedValue ||
                formattedValue.length === SSN_VALID_LENGTH ||
                formattedValue.length === SSN_VALID_LENGTH_SMALL
            ) {
                state.isValidSSN = true;
            }
        },

        toggleSelection: (state, action) => {
            const { id, checked } = action.payload;

            if (checked) {
                if (!state.mergePatientSelected.includes(id)) {
                    state.mergePatientSelected.push(id);
                }
            } else {
                state.mergePatientSelected = state.mergePatientSelected.filter(
                    (selectedId) => selectedId !== id
                );
            }

            // Header checkbox is true only if every patient item is selected
            state.isCheckedAll =
                Boolean(state.patientMergeList?.length) &&
                state.patientMergeList.every((item) =>
                    state.mergePatientSelected.includes(item.id)
                );
        },
        // Logic for Select All
        toggleSelectAll: (state, action) => {
            const checked = action.payload;
            state.isCheckedAll = checked;

            if (checked) {
                state.mergePatientSelected = state.patientMergeList.map((item) => item.id);
            } else {
                state.mergePatientSelected = [];
            }
        },
        changeActiveTab: (state, action) => {
            state.activeTab = action.payload;
        },
        updateCPTData: (state, action) => {
            const { name, data } = action.payload;
            let value=[];
            let label = null;

            if (Array.isArray(data) && data.length > 0) {
                label = data[0].id;
                value = data;
            } else {
                value = [];
                label = null
            }

            // Immer (built into RTK) allows us to mutate state directly
            state.otherBillingDetails[name] = label;
            state.otherBillingDetails[`${name}List`] = value;

            let cptResult = validateCPTICDCodes("cpt", state.otherBillingDetails);
            state.cptValidation = cptResult.validationMap;
            state.cptValidated = cptResult.isValidted;
            state.cptduplicated = cptResult.isDuplicated;
        },

        updateICDData: (state, action) => {
            const { name, data } = action.payload;
            let labelValue = null;
            let valueSet=[];

            if (Array.isArray(data) && data.length > 0) {
                labelValue = data[0].label || data[0].id || "";
                valueSet = data;
            } else {
                labelValue = null;
                valueSet = [];
            }

            state.otherBillingDetails[name] = labelValue;
            state.otherBillingDetails[`${name}List`] = valueSet;
            let dxResult = validateCPTICDCodes("dx",  state.otherBillingDetails);
            state.diagnosisValidation = dxResult.validationMap;
            state.diagnosisesValidated = dxResult.isValidted;
            state.diagnosisduplicated = dxResult.isDuplicated

        },

        updateBillingDate: (state, action) => {
            const { name, selected, isError } = action.payload;

            state.otherBillingDetails = {
                ...state.otherBillingDetails,
                [name]: selected
            }

            state.dataError = isError
        },

        handleBillingChange: (state, action) => {
            const { name, value } = action.payload;
            const { accidentNoId } = current(state);

            // Update the primary field
            state.otherBillingDetails[name] = value;

            // Handle the "Accident" logic: 
            // Reset dependent fields if value is empty or matches "No" ID
            if (name === "accident" && (value === accidentNoId || value === "")) {
                state.otherBillingDetails.accident_location_id = "";
                state.otherBillingDetails.other_accident = "";
            }
        },

        showInsAddNew:(state,action)=>{
            state.showInsuranceAddnew=action.payload
        },
        
        checkFormWarningData: (state, actions) => {
            let { patientBasicInfo, patientFormWarnings } = current(state)
            let { isInsurance = false } = actions.payload
            state.patientFormWarnings = checkFormWarning(patientBasicInfo, {}, patientFormWarnings, isInsurance);
            state.formWarningStatus = !state.formWarningStatus;
        },
        closePatientLedger: (state) => {
            state.patientLedger = {
                isOpen: false,
                loading: false,
                patientId: null,
                patientPaymentSummary: {},
                patientLeadgerSummary:{},
                claimsData: [],
                expandedClaims: {},
                error: null
            };
        },
        toggleLedgerClaimExpand: (state, action) => {
            const claimId = action.payload;
            const isCurrentlyExpanded = state.patientLedger.expandedClaims[claimId];
            
            // Toggle the claim expansion
            state.patientLedger.expandedClaims[claimId] = !isCurrentlyExpanded;
            
            // If closing the claim, also close all CPT accordions inside it
            if (isCurrentlyExpanded) {
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1 && state.patientLedger.claimsData[claimIndex].procedures) {
                    state.patientLedger.claimsData[claimIndex].procedures.forEach(proc => {
                        proc.isExpanded = false;
                    });
                }
            }
        },
        toggleProcedureExpand: (state, action) => {
            const { claimId, procedureId } = action.payload;
            const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
            if (claimIndex !== -1) {
                const procIndex = state.patientLedger.claimsData[claimIndex].procedures?.findIndex(p => p.cpt_id === procedureId);
                if (procIndex !== -1) {
                    state.patientLedger.claimsData[claimIndex].procedures[procIndex].isExpanded = 
                        !state.patientLedger.claimsData[claimIndex].procedures[procIndex].isExpanded;
                }
            }
        },
        setLedgerClaimLoading: (state, action) => {
            const { claimId, loading } = action.payload;
            state.patientLedger.claimsData = state.patientLedger.claimsData.map(claim =>
                claim.claim_id === claimId
                    ? { ...claim, proceduresLoading: loading, activitiesLoading: loading }
                    : claim
            );
        },
        updateLedgerClaimDetails: (state, action) => {
            const { claimId, procedures, activities } = action.payload;
            state.patientLedger.claimsData = state.patientLedger.claimsData.map(claim =>
                claim.claim_id === claimId
                    ? {
                        ...claim,
                        procedures: procedures || [],
                        activities: activities || [],
                        proceduresLoaded: true,
                        proceduresLoading: false,
                        activitiesLoading: false,
                    }
                    : claim
            );
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAllSelectInput.pending, (state) => {
                state.isSelectInputDataLoaded = false;
                state.isErrorLoadingData = false;
            })
            .addCase(getAllSelectInput.fulfilled, (state, action) => {
                const {
                    referringProvider,
                    renderingProvider,
                    serviceLocations,
                    patientTypes,
                    languagesList,
                    relationList,
                    referringProviderGroupList,
                    typeOfAccident,
                    listOfLocation
                } = action.payload;
                state.renderingProvider = renderingProvider;
                state.referringProvider = referringProvider;
                state.serviceLocations = serviceLocations;
                state.patientTypes = patientTypes;
                state.languagesList = languagesList;
                state.relationList = relationList;
                state.listOfLocation = listOfLocation
                state.referringProviderGroupList = referringProviderGroupList;
                state.typeOfAccident = typeOfAccident;
                let accident_no = typeOfAccident?.find((item => item.name.toLowerCase() == 'not related to accident'));
                state.accidentNoId = accident_no.id ?? "";
                state.isSelectInputDataLoaded = true;
            })
            .addCase(getAllSelectInput.rejected, (state) => {
                state.isSelectInputDataLoaded = false;
                state.isErrorLoadingData = true;
            })
            .addCase(getPatientBasicDetails.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getPatientBasicDetails.fulfilled, (state, actions) => {
                const { patientFormWarnings, relationList, renderingProvider, serviceLocations, } = current(state);
                let { isInsurance, patientInfoVariables, patientAddVariables, patientOtherBilling, assignmentBenifit,permitted_fields } = actions.payload;
                state.isInsuranceAdded=isInsurance;
                // Handle Service Location Logic
                if (patientInfoVariables?.practice_location) {
                    patientInfoVariables.service_location_value = `${patientInfoVariables.practice_location}||practice`;
                } else if (patientInfoVariables?.facility_location) {
                    patientInfoVariables.service_location_value = `${patientInfoVariables.facility_location}||facility`;
                }

                const {
                    patientInfoData,
                    setEmergencyRelationShip,
                    setEmergencySelf,
                    setGuarantorSelf,
                    disableGuarantorAddressFields
                } = generatePatientInfoData(patientInfoVariables, relationList, serviceLocations, renderingProvider,permitted_fields);

                state.disableGuarantorAddressFields = disableGuarantorAddressFields;
                state.patientInfoData = patientInfoData;
                state.duplicateDataSet.patientInfoData=patientInfoData;
                state.emergencySelf = setEmergencySelf;
                state.guaratorSelf = setGuarantorSelf;
                state.emergencyRelationShip = setEmergencyRelationShip;
                state.assignmentBenifit = assignmentBenifit;


                const basicData = generatePatientBasicDetails(patientAddVariables,permitted_fields);

                if (basicData) {
                    // Force clean strict null values instead of empty string literals
                    basicData.date_of_birth = (basicData.date_of_birth && moment(basicData.date_of_birth).isValid())
                        ? moment(basicData.date_of_birth).format("MM/DD/YYYY")
                        : null; // Changed from "" to null

                    basicData.date_of_death = (basicData.date_of_death && moment(basicData.date_of_death).isValid())
                        ? moment(basicData.date_of_death).format("MM/DD/YYYY")
                        : null; // Changed from "" to null
                }

                state.patientBasicInfo = basicData;
                state.duplicateDataSet.patientBasicInfo=basicData;

                const loadedSSN = String(basicData?.ssn ?? "");
                state.isValidSSN =
                    !loadedSSN ||
                    loadedSSN.length === SSN_VALID_LENGTH ||
                    loadedSSN.length === SSN_VALID_LENGTH_SMALL;


                const patientOtherBillingData = generateOtherBillingData(patientOtherBilling, assignmentBenifit,permitted_fields);

                state.otherBillingDetails = patientOtherBillingData;
                state.duplicateDataSet.otherBillingDetails=patientOtherBillingData;

                let cptResult = validateCPTICDCodes("cpt", patientOtherBillingData);
                state.cptValidation = cptResult.validationMap;
                state.cptValidated = cptResult.isValidted;
                state.cptduplicated = cptResult.isDuplicated;
                let dxResult= validateCPTICDCodes("dx", patientOtherBillingData);
                state.diagnosisValidation =  dxResult.validationMap;
                state.diagnosisesValidated = dxResult.isValidted;
                state.diagnosisduplicated = dxResult.isDuplicated

                state.patientFormWarnings = checkFormWarning(basicData, patientInfoData, patientFormWarnings, isInsurance);
                state.formWarningStatus = !state.formWarningStatus;
                state.isLoading = false;
            })
            .addCase(getPatientBasicDetails.rejected, (state, { payload }) => {
                state.isLoading = false;
                state.showNotification = true;
                state.notifyMessage = payload || i18n.t("errorMessages.commonError");
                state.statusTag = "error"
            });

        builder
            // --- PENDING: The request has started ---
            .addCase(activatePatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })

            // --- FULFILLED: The request succeeded ---
            .addCase(activatePatient.fulfilled, (state) => {
                state.isLoading = false;
                state.patientBasicInfo.patient_inactive = false;
                state.patientBasicInfo.patient_editable = true;
                state.patientBasicInfo.active = true;
                state.activePatientModal=false;
                state.showNotification = true;
                state.duplicateDataSet.patientBasicInfo=state.patientBasicInfo;
                state.notifyMessage = i18n.t("patientPages.patients.patientStatusActivated");
                state.statusTag = "success"
            })

            // --- REJECTED: The request failed ---
            .addCase(activatePatient.rejected, (state, { payload }) => {
                state.isLoading = false;
                state.showNotification = true;
                state.notifyMessage = payload || i18n.t("errorMessages.commonError");
                state.statusTag = "error"
            });

        builder
            .addCase(fetchMergePatients.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchMergePatients.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patientMergeList = action.payload;
            })
            .addCase(fetchMergePatients.rejected, (state, { payload }) => {
                state.showNotification = true;
                state.notifyMessage = payload || i18n.t("errorMessages.commonError");
                state.statusTag = "error"
                state.isLoading = false;
            });

        builder
            .addCase(validatePatients.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(validatePatients.fulfilled, (state, action) => {
                let { status } = action.payload;
                state.isLoading = false;
                if (status == 409) {
                    state.patientValidationModal = true;
                }
            })
            .addCase(validatePatients.rejected, (state, { payload }) => {
                state.showNotification = true;
                state.notifyMessage = payload || i18n.t("errorMessages.commonError");
                state.statusTag = "error"
                state.isLoading = false;
            });


        builder
            // ... existing cases ...
            .addCase(mergePatients.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(mergePatients.fulfilled, (state) => {
                state.isLoading = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t("patientPages.patients.patientMergeSucess");
                state.statusTag = "success"
                state.patientMergeModal = false;
                state.patientValidationModal = false;
                state.mergePatientSelected = [];
                state.patientMergeList = [];
            })
            .addCase(mergePatients.rejected, (state, { payload }) => {
                state.showNotification = true;
                state.notifyMessage = payload || i18n.t("errorMessages.commonError");
                state.statusTag = "error"
                state.isLoading = false;
            });
        builder
            // CPT Search Lifecycle
            .addCase(fetchCPTCodes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCPTCodes.fulfilled, (state, action) => {
                state.loading = false;
                state.cptList = action.payload;
            })
            .addCase(fetchCPTCodes.rejected, (state, action) => {
                state.loading = false;
                state.cptList = [];
                state.error = action.payload;
            });

        builder
            // CPT Search Lifecycle
            .addCase(fetchICDCodes.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchICDCodes.fulfilled, (state, action) => {
                state.loading = false;
                state.icdList = action.payload;
            })
            .addCase(fetchICDCodes.rejected, (state, action) => {
                state.loading = false;
                state.cptList = [];
                state.error = action.payload;
            });

        builder
            // PENDING: Triggered when the API call starts
            .addCase(fetchBillingCodesOnBlur.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            // FULFILLED: Triggered when API returns successfully
            .addCase(fetchBillingCodesOnBlur.fulfilled, (state, action) => {
                state.loading = false;
                const { name, data } = action.payload;

                if (data) {
                    // This replaces your updateDXCPTReduxValue logic
                    state.otherBillingDetails[name] = data[0].label || data[0].id;
                    state.otherBillingDetails[`${name}List`] = data;
                }
            })
            // REJECTED: Triggered if the API fails
            .addCase(fetchBillingCodesOnBlur.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        builder
            // patient payment Summary
            .addCase(patientPaymentSummary.pending, (state) => {
                state.patientLedger.loading = true;
                state.patientLedger.error = null;
            })
            .addCase(patientPaymentSummary.fulfilled, (state, action) => {
                state.patientLedger.loading = false;
                state.patientLedger.patientPaymentSummary = action.payload;
                state.patientLedger.error = null;
            })
            .addCase(patientPaymentSummary.rejected, (state, action) => {
                state.patientLedger.loading = false;
                state.patientLedger.error = action.payload;
            });

            builder
            // patient payment Summary
            .addCase(patientCreditBalanceView.pending, (state) => {
                state.patientLedger.loading = true;
                state.patientLedger.error = null;
            })
            .addCase(patientCreditBalanceView.fulfilled, (state, action) => {
                let {patient_credits,insurance_credits}=action.payload;
                state.patientLedger.loading = false;
                state.patientLedger.patientCredit = patient_credits;
                state.patientLedger.insuranceCredit = insurance_credits;
                state.patientLedger.error = null;
            })
            .addCase(patientCreditBalanceView.rejected, (state, action) => {
                state.patientLedger.loading = false;
                state.patientLedger.error = action.payload;
            });
        
            builder
            // Patient Ledger Summary
            .addCase(patientLedgerSummary.pending, (state) => {
                state.patientLedger.loading = true;
                state.patientLedger.error = null;
            })
            .addCase(patientLedgerSummary.fulfilled, (state, action) => {
                state.patientLedger.loading = false;
                state.patientLedger.patientLeadgerSummary = action.payload;
                state.patientLedger.insurance_tiers_summary=action.payload?.insurance_tiers_summary ?? [];
                state.patientLedger.error = null;
            })
            .addCase(patientLedgerSummary.rejected, (state, action) => {
                state.patientLedger.loading = false;
                state.patientLedger.error = action.payload;
            });

        builder
            // Patient Ledger List
            .addCase(getPatientLedgerList.pending, (state) => {
                state.patientLedger.isClaimLoading = true;
                state.patientLedger.error = null;
            })
            .addCase(getPatientLedgerList.fulfilled, (state, action) => {
                state.patientLedger.isClaimLoading = false;
                state.patientLedger.claimsData = action.payload;
                state.patientLedger.error = null;
            })
            .addCase(getPatientLedgerList.rejected, (state, action) => {
                state.patientLedger.isClaimLoading = false;
                state.patientLedger.error = action.payload;
            });

        builder
            // Load Claim Procedures
            .addCase(loadClaimProcedures.pending, (state, action) => {
                const claimId = action.meta.arg;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    state.patientLedger.claimsData[claimIndex].loading = true;
                }
            })
            .addCase(loadClaimProcedures.fulfilled, (state, action) => {
                const { claimId, procedures } = action.payload;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    state.patientLedger.claimsData[claimIndex].loading = false;
                    state.patientLedger.claimsData[claimIndex].procedures = procedures.map(proc => ({
                        ...proc,
                        activities: [],
                        activitiesLoaded: false,
                        activitiesLoading: false,
                        isExpanded: false
                    }));
                    state.patientLedger.claimsData[claimIndex].proceduresLoaded = true;
                }
                // Expand the claim on success
                state.patientLedger.expandedClaims[claimId] = true;
            })
            .addCase(loadClaimProcedures.rejected, (state, action) => {
                const claimId = action.meta.arg;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    state.patientLedger.claimsData[claimIndex].loading = false;
                    state.patientLedger.claimsData[claimIndex].procedures = [];
                }
            });

        builder
            // Load Procedure Activities
            .addCase(loadProcedureActivities.pending, (state, action) => {
                const { claimId, procedureId } = action.meta.arg;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    const procIndex = state.patientLedger.claimsData[claimIndex].procedures?.findIndex(p => p.id === procedureId);
                    if (procIndex !== -1) {
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activitiesLoading = true;
                    }
                }
            })
            .addCase(loadProcedureActivities.fulfilled, (state, action) => {
                const { claimId, procedureId, activities } = action.payload;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    const procIndex = state.patientLedger.claimsData[claimIndex].procedures?.findIndex(p => p.id === procedureId);
                    if (procIndex !== -1) {
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activitiesLoading = false;
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activities = activities;
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activitiesLoaded = true;
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].isExpanded = true;
                    }
                }
            })
            .addCase(loadProcedureActivities.rejected, (state, action) => {
                const { claimId, procedureId } = action.meta.arg;
                const claimIndex = state.patientLedger.claimsData.findIndex(c => c.claim_id === claimId);
                if (claimIndex !== -1) {
                    const procIndex = state.patientLedger.claimsData[claimIndex].procedures?.findIndex(p => p.id === procedureId);
                    if (procIndex !== -1) {
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activitiesLoading = false;
                        state.patientLedger.claimsData[claimIndex].procedures[procIndex].activities = [];
                    }
                }
            });

        builder
            // Get Activity of Procedure (CPT expand button)
            .addCase(getActivityOfProcedure.pending, (state, action) => {
                const { main_index, index } = action.meta.arg;
                if (state.patientLedger.claimsData[main_index]?.procedures?.[index]) {
                    state.patientLedger.claimsData[main_index].procedures[index].activitiesLoading = true;
                }
            })
            .addCase(getActivityOfProcedure.fulfilled, (state, action) => {
                const { data, index, main_index } = action.payload;
                if (state.patientLedger.claimsData[main_index]?.procedures?.[index]) {
                    state.patientLedger.claimsData[main_index].procedures[index].activitiesLoading = false;
                    state.patientLedger.claimsData[main_index].procedures[index].activities = data || [];
                    state.patientLedger.claimsData[main_index].procedures[index].activitiesLoaded = true;
                    state.patientLedger.claimsData[main_index].procedures[index].isExpanded = true;
                }
            })
            .addCase(getActivityOfProcedure.rejected, (state, action) => {
                const { main_index, index } = action.meta.arg;
                if (state.patientLedger.claimsData[main_index]?.procedures?.[index]) {
                    state.patientLedger.claimsData[main_index].procedures[index].activitiesLoading = false;
                    state.patientLedger.claimsData[main_index].procedures[index].activities = [];
                }
            });

            builder.addCase(updatePatientData.pending,(state)=>{
                state.isSaveLoading = true;
            })
            builder.addCase(updatePatientData.fulfilled,(state,action)=>{
                const payload = action.payload || {};
                state.isSaveLoading = false;
                state.showNotification = true;
                state.notifyMessage = i18n.t("patientbeta.api_success.patient_update_success");
                state.statusTag = "success";
                state.activePatientModal=false;
                state.inactivePatientModal=false;
                if(payload && payload.isClose){
                    state.isClaimClose = true;
                }

                if(payload && !payload.active){
                    state.isClaimClose = true; 
                }
                
            })

            builder.addCase(updatePatientData.rejected, (state, action) => {
                state.isSaveLoading = false;
            
                // 1. Safe Fallback: Handle cases where payload might be undefined, a string, or an object
                const payload = action.payload || {};

                if(payload && payload.isClose){
                    state.isClaimClose = true;
                }
                
                // 2. Extract message whether payload is an object or a direct string
                const errorMessage = typeof payload === 'string' 
                    ? payload 
                    : (payload.message || i18n.t("errorMessages.commonError"));
            
                // 3. Trigger notification safely
                state.showNotification = true;
                state.notifyMessage = errorMessage;
                state.statusTag = "error";
            
                // 4. Safely handle the validation booleans (ensuring no typo mismatches)
                // Using double exclamation (!!) ensures they strictly cast to true/false
                state.patientInfoValidEmail = !!payload.patientInfoValidEmail;
                state.patientInfoValidEmergencyEmail = !!payload.patientInfoValidEmergencyEmail;
                state.patientInfoValidGuarantorEmail = !!payload.patientInfoValidGuarantorEmail;
            });
    },
});

export const {
    resetToInitial,
    showModal,
    setMergePatientSelected,
    basicFieldChange,
    onHandleChangeValue,
    onHandleLocationDataChange,
    changeActiveTab,
    onHandleSSNChange,
    resetNotifyWindow,
    toggleSelection, toggleSelectAll,
    clearList, updateBillingDate,
    updateCPTData, updateICDData,
    handleBillingChange,
    showInsAddNew,
    checkFormWarningData,
    closePatientLedger,
    toggleLedgerClaimExpand,
    toggleProcedureExpand,
    setLedgerClaimLoading,
    updateLedgerClaimDetails,
    showPatientLedgerModal,
} = patientBetaSlice.actions;

export default patientBetaSlice.reducer;